// Pure damage / mana math used by fightSaga.
// Kept separate from the saga so it's trivially unit-testable.

import { getEffectiveStats } from './equipment';

export const MANA_COST = {
    BASIC_ATTACK: 2,
    SPECIAL_ATTACK: 10,
    MOVE_REGEN: 0.05,   // fraction of maxMana regenerated per move
};

// Basic attack damage = effective strength minus monster defense, floor 1.
export const calculateBasicDamage = (hero, monster) => {
    const strength = getEffectiveStats(hero).strength || 0;
    const defense = monster?.stats?.defense || 0;
    return Math.max(1, strength - defense);
};

// Stun special: 80 % of effective strength, floored at 1.
export const calculateStunDamage = (hero) => {
    const strength = getEffectiveStats(hero).strength || 0;
    return Math.max(1, Math.floor(strength * 0.8));
};

// Armor-penetration special: 150 % of strength baseline, with a 30 %
// crit roll multiplying by another 1.5×. Returns { damage, isCritical }.
// `roll` is injectable so tests can pin the random value.
export const calculateArmorPenDamage = (hero, roll = Math.random) => {
    const strength = getEffectiveStats(hero).strength || 0;
    const base = strength * 1.5;
    const isCritical = roll() * 100 <= 30;
    const damage = isCritical ? Math.floor(base * 1.5) : Math.floor(base);
    return { damage: Math.max(1, damage), isCritical };
};

// Mana check + consumption helpers — pure for tests; the saga still
// uses its own generator wrapper to dispatch the consume action.
export const hasEnoughMana = (hero, isSpecialAttack = false) => {
    const cost = isSpecialAttack ? MANA_COST.SPECIAL_ATTACK : MANA_COST.BASIC_ATTACK;
    return (hero?.stats?.mana || 0) >= cost;
};

export const manaAfterAttack = (hero, isSpecialAttack = false) => {
    const cost = isSpecialAttack ? MANA_COST.SPECIAL_ATTACK : MANA_COST.BASIC_ATTACK;
    return Math.max(0, (hero?.stats?.mana || 0) - cost);
};

// Regen on movement: ceil(maxMana × 5 %), capped at maxMana.
export const regenManaAmount = (hero) => {
    const maxMana = hero?.stats?.maxMana || 0;
    if (!maxMana) return 0;
    const current = hero.stats.mana || 0;
    const regen = Math.ceil(maxMana * MANA_COST.MOVE_REGEN);
    return Math.min(maxMana, current + regen) - current;
};
